import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtNum, errMsg, fileUrl } from "@finance/lib/api";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@finance/components/ui/dialog";
import { EfdTimeField } from "@finance/components/EfdTimeField";
import { UserCircle, Receipt, ClockCountdown, Prohibit } from "@phosphor-icons/react";

export const CATS = [
  { v: "staff_allowance", label: "Staff allowance", icon: UserCircle, cls: "bg-violet-100 text-violet-700", hint: "Advance to employee (GL 1460). Partial EFDs; balance grossed up into payroll." },
  { v: "after_efd", label: "Payment after EFD", icon: Receipt, cls: "bg-emerald-100 text-emerald-700", hint: "EFD in hand. Immediate (Cr bank) or credit (Cr AP + credit days)." },
  { v: "before_efd", label: "Payment before EFD", icon: ClockCountdown, cls: "bg-amber-100 text-amber-700", hint: "Advance to Dr 1470 Supplier Advances \u2013 Pending EFD; cleared when the EFD arrives." },
  { v: "no_efd", label: "No EFD expected", icon: Prohibit, cls: "bg-rose-100 text-rose-700", hint: "Full amount to expense, no VAT, Non-deductible. Signed payment voucher mandatory." },
];

export function Attachments({ e }) {
  const docs = e.documents || [];
  const [sel, setSel] = useState(docs[0]);
  if (!docs.length) return <div className="rounded-sm border border-dashed border-red-200 bg-red-50/50 p-4 text-center text-sm font-semibold text-red-600" data-testid="xp-no-attachments">No attachments</div>;
  const url = (d) => fileUrl(`expenses/${e.id}/documents/${d.id}/file`);
  return (
    <div data-testid="xp-attachments">
      <div className="lx-label mb-1">Attachments ({docs.length})</div>
      <div className="mb-2 flex flex-wrap gap-1">{docs.map((d) => <button key={d.id} onClick={() => setSel(d)} className={`rounded-sm border px-2 py-0.5 text-xs ${sel?.id === d.id ? "border-[#1B4383] text-[#1B4383]" : ""}`}>{d.filename}{d.kind === "signed_voucher" ? " \u00b7 signed PV" : ""}</button>)}</div>
      {sel && ((sel.content_type || "").startsWith("image/") ? <img alt="" src={url(sel)} className="max-h-72 w-full rounded-sm border object-contain" /> : <iframe title="att" src={url(sel)} className="h-72 w-full rounded-sm border" />)}
    </div>
  );
}

export function EfdFields({ f, setF, approved }) {
  const total = Number(f.taxable || 0) + Number(f.exempt || 0) + Number(f.vat || 0);
  const vatOk = f.non_vat_supplier ? !Number(f.vat || 0) : Math.abs(Number(f.vat || 0) - Number(f.taxable || 0) * 0.18) <= 1;
  return (
    <div className="space-y-2 rounded-sm border border-gray-200 p-3" data-testid="xp-efd-fields">
      <div className="grid grid-cols-3 gap-2">
        {["taxable", "exempt", "vat"].map((k) => <label key={k} className="text-xs capitalize">{k === "taxable" ? "Taxable (excl. VAT)" : k === "vat" ? "VAT" : "Exempt"}<Input type="number" className="mt-1" data-testid={`xp-efd-${k}`} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>)}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span>EFD Total <b className="lx-num" data-testid="xp-efd-total">TZS {fmtNum(total)}</b></span>
        {!vatOk && <span className="font-semibold text-red-600">VAT must be 18% of taxable ({fmtNum(Number(f.taxable || 0) * 0.18)})</span>}
        {approved != null && Math.abs(total - approved) > 0.01 && <span className="text-amber-600" data-testid="xp-efd-diff">Differs from approved by {fmtNum(total - approved)}</span>}
        <label className="flex items-center gap-1"><input type="checkbox" data-testid="xp-efd-nonvat" checked={!!f.non_vat_supplier} onChange={(e) => setF({ ...f, non_vat_supplier: e.target.checked, vat: e.target.checked ? 0 : f.vat })} />Non-VAT supplier</label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs">EFD code<Input className="mt-1" data-testid="xp-efd-code" value={f.efd_code || ""} onChange={(e) => setF({ ...f, efd_code: e.target.value })} /></label>
        <div className="text-xs">EFD time<EfdTimeField testid="xp-efd-time" value={f.efd_time} onChange={(v) => setF({ ...f, efd_time: v })} /></div>
      </div>
    </div>
  );
}

export function Splits({ splits, setSplits, accounts, target }) {
  const sum = splits.reduce((s, x) => s + Number(x.amount || 0), 0);
  return (
    <div className="space-y-1" data-testid="xp-splits">
      <div className="lx-label">Expense GL (picked manually) \u00b7 total {fmtNum(sum)} / {fmtNum(target)}</div>
      {splits.map((s, i) => (
        <div key={i} className="flex gap-2">
          <select className="h-9 flex-1 rounded-sm border px-2 text-sm" data-testid={`xp-split-acct-${i}`} value={s.account_code} onChange={(e) => setSplits(splits.map((x, j) => (j === i ? { ...x, account_code: e.target.value } : x)))}>
            <option value="">Select GL\u2026</option>
            {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} \u00b7 {a.name}</option>)}
          </select>
          <Input type="number" className="h-9 w-36 text-right" data-testid={`xp-split-amt-${i}`} value={s.amount} onChange={(e) => setSplits(splits.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
        </div>
      ))}
      <button className="text-xs text-[#1B4383]" onClick={() => setSplits([...splits, { account_code: "", amount: "" }])}>+ split</button>
    </div>
  );
}

export function WhtPanel({ w, setW, base, total }) {
  const amt = w.on ? Math.round(base * (Number(w.rate) || 0)) / 100 : 0;
  const cash = w.method === "net_down" ? total - amt : total;
  return (
    <div className="space-y-2 rounded-sm border border-indigo-200 bg-indigo-50/40 p-3" data-testid="xp-wht-panel">
      <label className="flex items-center gap-2 text-sm font-semibold text-indigo-700"><input type="checkbox" data-testid="xp-wht-toggle" checked={w.on} onChange={(ev) => setW({ ...w, on: ev.target.checked })} />Apply withholding tax (WHT)</label>
      {w.on && <>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <label>Rate %<Input type="number" className="mt-1 h-8 w-20" data-testid="xp-wht-rate" value={w.rate} onChange={(ev) => setW({ ...w, rate: ev.target.value })} /></label>
          {[["net_down", "Net down (pay total − WHT)"], ["gross_up", "Gross up (Somcore bears WHT)"]].map(([v, l]) => <label key={v} className="flex items-center gap-1"><input type="radio" data-testid={`xp-wht-${v}`} checked={w.method === v} onChange={() => setW({ ...w, method: v })} />{l}</label>)}
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs" data-testid="xp-wht-preview">
          <div>Gross <b className="lx-num block">TZS {fmtNum(total)}</b></div>
          <div>WHT ({w.rate || 0}% of excl. VAT) <b className="lx-num block" data-testid="xp-wht-amount">TZS {fmtNum(amt)}</b></div>
          <div>Cash payable <b className="lx-num block" data-testid="xp-wht-net">TZS {fmtNum(cash)}</b></div>
        </div>
      </>}
    </div>
  );
}

export function AssignDialog({ e, onClose, onDone }) {
  const [cat, setCat] = useState("");
  const [sett, setSett] = useState("immediate");
  const [days, setDays] = useState("");
  const [efd, setEfd] = useState({});
  const [cardId, setCardId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [cards, setCards] = useState([]);
  const [splits, setSplits] = useState([{ account_code: "", amount: e.amount_tzs }]);
  const [wht, setWht] = useState({ on: false, rate: "5", method: "net_down" });
  useEffect(() => {
    api.get("/wht/settings").then((r) => setWht((w) => ({ ...w, rate: String(r.data.resident_rate) }))).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/gl/accounts").then((r) => setAccounts((r.data || []).filter((a) => a.type === "Expense"))).catch(() => {});
    api.get("/xp/cards").then((r) => { setCards(r.data); const d = r.data.find((c) => c.is_default); if (d) setCardId(d.id); });
  }, []);
  const needsEfd = cat === "after_efd" || sett === "card";
  const target = needsEfd ? Number(efd.taxable || 0) + Number(efd.exempt || 0) : e.amount_tzs;
  const whtAllowed = (cat === "after_efd" || cat === "no_efd") && sett !== "card";
  const whtTotal = cat === "after_efd" ? target + Number(efd.vat || 0) : e.amount_tzs;
  const submit = async () => {
    try {
      await api.post(`/xp/expenses/${e.id}/assign`, { category: cat, settlement: sett, credit_days: days === "" ? null : Number(days), card_id: cardId || null,
        gl_splits: splits.map((s) => ({ account_code: s.account_code, amount: Number(s.amount || 0) })), ...efd,
        taxable: Number(efd.taxable || 0), exempt: Number(efd.exempt || 0), vat: Number(efd.vat || 0),
        wht_applicable: wht.on && whtAllowed, wht_rate: Number(wht.rate || 0), wht_method: wht.method });
      toast.success("Assigned"); onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto" data-testid="xp-assign-dialog">
        <DialogHeader><DialogTitle className="font-display">Assign {e.xp_no} \u00b7 TZS {fmtNum(e.amount_tzs)}</DialogTitle></DialogHeader>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">{CATS.map((c) => (
              <button key={c.v} data-testid={`xp-cat-${c.v}`} onClick={() => { setCat(c.v); if (c.v === "staff_allowance") setSett("immediate"); }}
                className={`flex gap-2 rounded-sm border p-2 text-left ${cat === c.v ? "border-[#1B4383] ring-1 ring-[#1B4383]" : "border-gray-200"}`}>
                <input type="radio" readOnly checked={cat === c.v} className="mt-1" /><c.icon size={20} className="mt-0.5 shrink-0 text-[#1B4383]" />
                <span><span className="block text-sm font-semibold">{c.label}</span><span className="text-[11px] text-gray-500">{c.hint}</span></span>
              </button>))}</div>
            {cat && cat !== "staff_allowance" && (
              <div className="flex flex-wrap gap-3 text-sm">{["immediate", "credit", ...(cat === "after_efd" ? ["card"] : [])].map((s) => <label key={s} className="flex items-center gap-1 capitalize"><input type="radio" data-testid={`xp-sett-${s}`} checked={sett === s} onChange={() => setSett(s)} />{s === "card" ? "Credit card" : s}</label>)}</div>)}
            {sett === "credit" && <label className="block text-xs">Credit days *<Input type="number" className="mt-1" data-testid="xp-credit-days" value={days} onChange={(ev) => setDays(ev.target.value)} /></label>}
            {sett === "card" && <select className="h-9 w-full rounded-sm border px-2 text-sm" data-testid="xp-card" value={cardId} onChange={(ev) => setCardId(ev.target.value)}><option value="">Select card\u2026</option>{cards.map((c) => <option key={c.id} value={c.id}>{c.label} \u2022\u2022{c.last4} ({c.currency})</option>)}</select>}
            {needsEfd && <EfdFields f={efd} setF={setEfd} approved={e.amount_tzs} />}
            {cat && <Splits splits={splits} setSplits={setSplits} accounts={accounts} target={target} />}
            {whtAllowed && <WhtPanel w={wht} setW={setWht} base={target} total={whtTotal} />}
            {cat === "no_efd" && !e.voucher_signed && <div className="text-xs text-red-600">Signed payment voucher not yet uploaded \u2014 required before any payment posts.</div>}
            <Button className="w-full bg-[#1B4383] hover:bg-[#122E5C]" disabled={!cat} data-testid="xp-assign-confirm" onClick={submit}>Assign</Button>
          </div>
          <div className="space-y-3 text-sm">
            <div><span className="lx-label">Purpose</span><div>{e.purpose || e.description}</div></div>
            {e.links && <div><span className="lx-label">Links</span><div className="break-all text-xs">{e.links}</div></div>}
            <div><span className="lx-label">Approver's comment</span><div data-testid="xp-approver-comment">{e.approver_comment || "\u2014"}</div></div>
            <Attachments e={e} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
